import {
  ArrowDownRight,
  ArrowUpRight,
  Bell,
  Ban,
  Activity,
  Target,
  type LucideIcon,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { cn, humanizePercent, humanizeCount } from '@/lib/utils';
import type {
  DashboardKpis,
  DashboardKpiTrend,
  DashboardEngagementScoreDistribution,
} from '../types';

/**
 * Plan-target KPI cards for the notification health dashboard.
 *
 * Shows three "must improve" metrics from the redesign plan
 * (open rate, dismiss rate, p99 sends/user) against their
 * baseline and target values, plus the engagement score
 * distribution across users with a trusted signal.
 */
export function KpiCards({ kpis }: { kpis: DashboardKpis }) {
  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
      <KpiCard
        label="Open rate · 7d"
        trend={kpis.openRate}
        format="percent"
        icon={Bell}
        hint={`${humanizeCount(kpis.totalOpens7d)} opens / ${humanizeCount(kpis.totalSends7d)} sends`}
      />
      <KpiCard
        label="Dismiss rate · 7d"
        trend={kpis.dismissRate}
        format="percent"
        icon={Ban}
        hint={`${humanizeCount(kpis.totalDismissals7d)} dismissed or timed out`}
      />
      <KpiCard
        label="p99 sends / user · 7d"
        trend={kpis.p99SendsPerUser}
        format="count"
        icon={Activity}
        hint="per-user 7-day send count p99"
      />
      <EngagementScoreCard distribution={kpis.engagementScoreDistribution} />
    </div>
  );
}

interface KpiCardProps {
  label: string;
  trend: DashboardKpiTrend;
  format: 'percent' | 'count';
  icon: LucideIcon;
  hint?: string;
}

function KpiCard({ label, trend, format, icon: Icon, hint }: KpiCardProps) {
  const fmt = (v: number) =>
    format === 'percent' ? humanizePercent(v) : humanizeCount(Math.round(v));

  // Trend direction: for 'up' metrics (open rate) an increase is good.
  // For 'down' metrics (dismiss rate, p99 sends) a decrease is good.
  const deltaIsImprovement =
    trend.direction === 'up' ? trend.delta >= 0 : trend.delta <= 0;
  const deltaTone = deltaIsImprovement
    ? 'text-[hsl(var(--success))]'
    : 'text-destructive';

  const progressClamped = Math.max(0, Math.min(1.25, trend.percentToTarget));
  const progressPct = Math.min(100, progressClamped * 100);
  const hitTarget = trend.percentToTarget >= 1;

  return (
    <Card className={cn('overflow-hidden', hitTarget && 'border-[hsl(var(--success))]/40')}>
      <CardContent className="flex flex-col gap-2 p-4">
        <div className="flex items-center justify-between text-xs font-medium uppercase tracking-wider text-muted-foreground">
          <span>{label}</span>
          <Icon className="h-4 w-4 text-muted-foreground" aria-hidden />
        </div>

        <div className="font-display text-2xl font-extrabold">{fmt(trend.current)}</div>

        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span className={cn('inline-flex items-center gap-0.5 font-medium', deltaTone)}>
            {trend.delta >= 0 ? (
              <ArrowUpRight className="h-3 w-3" />
            ) : (
              <ArrowDownRight className="h-3 w-3" />
            )}
            {format === 'percent'
              ? humanizePercent(Math.abs(trend.delta))
              : humanizeCount(Math.round(Math.abs(trend.delta)))}
          </span>
          <span className="text-muted-foreground/60">vs prior 7d</span>
        </div>

        <div className="mt-1 flex items-center justify-between text-[10px] text-muted-foreground">
          <span>baseline {fmt(trend.baseline)}</span>
          <span className="inline-flex items-center gap-1">
            <Target className="h-3 w-3" aria-hidden />
            target {fmt(trend.target)}
          </span>
        </div>

        <div
          aria-hidden
          className="mt-1 h-1.5 overflow-hidden rounded-full bg-muted"
          title={`${Math.round(trend.percentToTarget * 100)}% of the way to target`}
        >
          <div
            className={cn(
              'h-full transition-all',
              hitTarget ? 'bg-[hsl(var(--success))]' : 'bg-primary',
            )}
            style={{ width: `${progressPct}%` }}
          />
        </div>

        {hint && <div className="text-[11px] text-muted-foreground">{hint}</div>}
      </CardContent>
    </Card>
  );
}

function EngagementScoreCard({
  distribution,
}: {
  distribution: DashboardEngagementScoreDistribution;
}) {
  const { p25, p50, p75, p99, mean, signalEnabledCount, signalDisabledCount } = distribution;
  const total = signalEnabledCount + signalDisabledCount;
  const signalPct = total > 0 ? signalEnabledCount / total : 0;

  return (
    <Card className="overflow-hidden">
      <CardContent className="flex flex-col gap-2 p-4">
        <div className="flex items-center justify-between text-xs font-medium uppercase tracking-wider text-muted-foreground">
          <span>Engagement score</span>
          <Target className="h-4 w-4 text-muted-foreground" aria-hidden />
        </div>

        <div className="font-display text-2xl font-extrabold">{mean.toFixed(2)}</div>

        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span>mean · p50 {p50.toFixed(2)}</span>
        </div>

        <div className="mt-1 grid grid-cols-4 gap-1 text-center text-[10px] text-muted-foreground">
          <div>
            <div className="font-mono text-foreground">{p25.toFixed(2)}</div>
            <div>p25</div>
          </div>
          <div>
            <div className="font-mono text-foreground">{p50.toFixed(2)}</div>
            <div>p50</div>
          </div>
          <div>
            <div className="font-mono text-foreground">{p75.toFixed(2)}</div>
            <div>p75</div>
          </div>
          <div>
            <div className="font-mono text-foreground">{p99.toFixed(2)}</div>
            <div>p99</div>
          </div>
        </div>

        <div className="mt-1 text-[11px] text-muted-foreground">
          {signalEnabledCount} / {total} users with trusted signal ({humanizePercent(signalPct)})
        </div>
      </CardContent>
    </Card>
  );
}
